import { useState } from 'react';
import { useCart } from '../../context/CartContext';
import { useAppDataContext } from '../../context/AppDataContext';
import { useTranslation } from '../../i18n/index.jsx';
import Icon from './Icon';

/**
 * CartFloatingButton — Floating cart FAB + slide-up cart drawer
 * Shows cart badge count, allows creating/switching/deleting carts,
 * and viewing cart contents before opening POS.
 */
export default function CartFloatingButton({ onOpenPOS }) {
  const { t } = useTranslation();
  const { customers } = useAppDataContext();
  const {
    cartList,
    cartCount,
    activeCartKey,
    activeCart,
    activeCartItems,
    createCart,
    switchCart,
    deleteCart,
    removeFromCart,
    updateQty,
    clearActiveCart,
  } = useCart();

  const [open, setOpen] = useState(false);
  const [showNewCart, setShowNewCart] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  // Don't render FAB if no carts exist and POS button exists elsewhere
  // Actually, always show FAB so users can create carts
  const handleOpenPOS = () => {
    setOpen(false);
    onOpenPOS?.();
  };

  const handleCreateWalkin = () => {
    createCart(null, null); // walkin cart
    setShowNewCart(false);
    setCustomerSearch('');
  };

  const handleSelectCustomer = (customer) => {
    createCart(customer.id, customer.name || customer.nameJa);
    setShowNewCart(false);
    setCustomerSearch('');
  };

  const handleDeleteCart = (e, cartKey) => {
    e.stopPropagation();
    deleteCart(cartKey);
  };

  const filteredCustomers = customerSearch.trim()
    ? (customers || []).filter(c => {
        const s = customerSearch.toLowerCase();
        return `${c.name || ''} ${c.nameJa || ''} ${c.company || ''} ${c.phone || ''}`.toLowerCase().includes(s);
      }).slice(0, 8)
    : [];

  const formatPrice = (n) => `¥${(n || 0).toLocaleString()}`;

  return (
    <>
      {/* Floating Cart Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 left-4 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/30 flex items-center justify-center active:scale-90 transition-transform"
        >
          <Icon name="ShoppingCart" size={24} className="text-white" />
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1">
              {cartCount}
            </span>
          )}
        </button>
      )}

      {/* Cart Drawer (slide-up panel) */}
      {open && (
        <div className="fixed inset-0 z-50 flex flex-col">
          {/* Backdrop */}
          <div className="flex-1 bg-black/60 backdrop-blur-sm" onClick={() => { setOpen(false); setShowNewCart(false); }} />

          {/* Panel */}
          <div className="bg-t-card border-t border-t-border rounded-t-2xl safe-area-bottom animate-slideUp max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-t-border flex-shrink-0">
              <div className="flex items-center gap-2">
                <Icon name="ShoppingCart" size={18} className="text-emerald-400" />
                <span className="text-t-primary font-bold text-sm">
                  {t('cart.badge', 'カート')}
                </span>
                {cartCount > 0 && (
                  <span className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-2 py-0.5 rounded-full">
                    {cartCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowNewCart(!showNewCart)}
                  className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium flex items-center gap-1"
                >
                  <Icon name="Plus" size={12} />
                  {t('cart.newCart', '新規')}
                </button>
                <button
                  onClick={() => { setOpen(false); setShowNewCart(false); }}
                  className="w-8 h-8 rounded-full bg-t-input flex items-center justify-center"
                >
                  <Icon name="X" size={16} className="text-t-tertiary" />
                </button>
              </div>
            </div>

            {/* New Cart Creation */}
            {showNewCart && (
              <div className="px-4 py-3 border-b border-t-border bg-t-surface/50 flex-shrink-0">
                <div className="text-t-secondary text-xs font-medium mb-2">
                  {t('cart.selectCustomer', '顧客を選択')}
                </div>
                {/* Walk-in quick button */}
                <button
                  onClick={handleCreateWalkin}
                  className="w-full mb-2 px-3 py-2.5 bg-t-card border border-t-border rounded-xl text-left flex items-center gap-3 hover:bg-t-card-hover transition"
                >
                  <div className="w-8 h-8 rounded-full bg-rose-500/20 flex items-center justify-center text-sm">
                    🚶
                  </div>
                  <div>
                    <div className="text-t-primary text-sm font-medium">Walk-in</div>
                    <div className="text-t-tertiary text-xs">{t('cart.walkinDesc', '一般のお客様')}</div>
                  </div>
                </button>
                {/* Customer search */}
                <div className="relative">
                  <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-t-tertiary" />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    placeholder={t('cart.searchCustomer', '顧客名で検索...')}
                    className="w-full bg-t-card border border-t-border rounded-xl pl-9 pr-3 py-2 text-t-primary text-sm placeholder:text-t-tertiary focus:border-emerald-500 focus:outline-none transition"
                    autoFocus
                  />
                </div>
                {/* Search results */}
                {filteredCustomers.length > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {filteredCustomers.map(c => (
                      <button
                        key={c.id}
                        onClick={() => handleSelectCustomer(c)}
                        className="w-full px-3 py-2 bg-t-card border border-t-border rounded-lg text-left flex items-center gap-2 hover:bg-t-card-hover transition"
                      >
                        <div className="w-7 h-7 rounded-full bg-teal-500/20 flex items-center justify-center text-xs text-teal-400 font-bold">
                          {(c.name || c.nameJa || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-t-primary text-sm font-medium truncate">{c.name || c.nameJa}</div>
                          {c.company && <div className="text-t-tertiary text-xs truncate">{c.company}</div>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Cart List */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {cartList.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-3xl mb-2">🛒</div>
                  <div className="text-t-secondary text-sm">{t('cart.empty', 'カートが空です')}</div>
                  <div className="text-t-tertiary text-xs mt-1">{t('cart.emptyHint', '「＋新規」でカートを作成')}</div>
                </div>
              ) : (
                cartList.map(cart => {
                  const isActive = cart.key === activeCartKey;
                  const total = cart.items.reduce((sum, i) => sum + (i.price || 0) * i.qty, 0);
                  return (
                    <button
                      key={cart.key}
                      onClick={() => {
                        switchCart(cart.key);
                      }}
                      className={`w-full text-left rounded-xl border transition overflow-hidden ${
                        isActive
                          ? 'bg-emerald-500/10 border-emerald-500/30'
                          : 'bg-t-card border-t-border hover:bg-t-card-hover'
                      }`}
                    >
                      <div className="px-3 py-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            {isActive && (
                              <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                            )}
                            <span className={`text-sm font-medium truncate ${isActive ? 'text-emerald-400' : 'text-t-primary'}`}>
                              {cart.customerName || 'Walk-in'}
                            </span>
                            <span className="text-xs text-t-tertiary flex-shrink-0">
                              {cart.itemCount}{t('cart.items', '件')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-sm font-bold ${isActive ? 'text-emerald-400' : 'text-t-primary'}`}>
                              {formatPrice(total)}
                            </span>
                            <button
                              onClick={(e) => handleDeleteCart(e, cart.key)}
                              className="w-6 h-6 rounded-full bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 transition"
                            >
                              <Icon name="X" size={12} className="text-red-400" />
                            </button>
                          </div>
                        </div>
                        {/* Show items preview for active cart */}
                        {isActive && cart.items.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-emerald-500/15 space-y-1">
                            {cart.items.slice(0, 3).map(item => (
                              <div key={item.sku} className="flex items-center justify-between text-xs">
                                <span className="text-t-secondary truncate flex-1 mr-2">{item.name}</span>
                                <span className="text-t-tertiary flex-shrink-0">×{item.qty}</span>
                              </div>
                            ))}
                            {cart.items.length > 3 && (
                              <div className="text-xs text-t-tertiary">
                                +{cart.items.length - 3} {t('cart.moreItems', 'more')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer: Open POS button */}
            {activeCart && activeCartItems.length > 0 && (
              <div className="flex-shrink-0 px-4 py-3 border-t border-t-border">
                <button
                  onClick={handleOpenPOS}
                  className="w-full py-3 bg-emerald-500 text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                >
                  <Icon name="ArrowRight" size={16} />
                  {t('cart.openPOS', 'レジへ進む')}
                  <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                    {formatPrice(activeCartItems.reduce((sum, i) => sum + (i.price || 0) * i.qty, 0))}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
